import * as React from "react";
import clsx from "clsx";
import * as s from "./UnsavedChangesChip.style.css";

export type UnsavedChangesChipProps = {
  /** Number of unsaved changes. When 0/undefined and `forceShow` is false, renders nothing. */
  count?: number;
  /** Override label. Defaults to "N unsaved change(s)". */
  label?: string;
  className?: string;
  /** When set, chip is clickable (e.g. scroll to first unsaved row). */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
};

/**
 * Scroll the first `[data-unsaved]` row inside a CollapsibleSection into view.
 * Expands the section first when collapsed (for `unmountCollapsed` bodies).
 */
export function scrollToFirstUnsavedInSection(
  sectionId: string,
  opts?: { expanded?: boolean; onExpand?: () => void },
) {
  const scroll = () => {
    document
      .getElementById(sectionId)
      ?.querySelector<HTMLElement>("[data-unsaved]")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (opts?.expanded === false && opts.onExpand) {
    opts.onExpand();
    window.setTimeout(scroll, 280);
    return;
  }
  scroll();
}

export function UnsavedChangesChip({
  count,
  label,
  className,
  onClick,
  title,
}: UnsavedChangesChipProps) {
  const n = count ?? 0;
  if (n <= 0 && !label) return null;

  const text =
    label ??
    `${n} unsaved ${n === 1 ? "change" : "changes"}`;

  const classes = clsx(s.chip, onClick && s.chipButton, className);

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
      >
        {text}
      </button>
    );
  }

  return <span className={classes}>{text}</span>;
}
